import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Fragment } from "react/jsx-runtime";
import { withTimeSystem } from "../../constants/orbitMetadata";
import { getRecordEventId } from "../../utils/recordValues";
import {
  ConjunctionSummaryCard,
  type ConjunctionSummaryCardField,
} from "../conjunctions/ConjunctionSummaryCard";
import {
  getConjunctionMetricTone,
  getDisplayValue,
  getFormattedDisplayValue,
  getNestedDisplayValue,
  getNestedValue,
  getRiskLabel,
  getProbabilityFamilyTone,
  getRiskTone,
  type ConjunctionRiskConfig,
  type RiskTone,
} from "../conjunctions/conjunctionUiUtils";
import {
  ContactsByOperatorButton,
  type ContactsByOperatorSelection,
} from "../ContactsByOperatorButton";
import { formatDate } from "../events/eventEvolutionUtils";
import {
  ActionsColumnItem,
  TABLE_ACTION,
  type TableActionType,
} from "../table/TableActionColumn";
import { CompactObject } from "./CompactObject";
import { getConjunctionId } from "./FleetStatusCardList";

const riskPillClassName: Record<RiskTone, string> = {
  danger: "bg-red-100 text-red-700",
  muted: "bg-slate-100 text-slate-600",
  warning: "bg-amber-100 text-amber-700",
};

const riskCardClassName: Record<RiskTone, string> = {
  danger: "border-red-200 shadow-red-100/70",
  muted: "border-slate-200 shadow-slate-100/70",
  warning: "border-amber-200 shadow-amber-100/70",
};

const riskAccentClassName: Record<RiskTone, string> = {
  danger: "bg-red-500",
  muted: "bg-slate-300",
  warning: "bg-amber-400",
};

interface FleetStatusCardProps {
  conjunction: unknown;
  formatters: unknown;
  riskConfig?: ConjunctionRiskConfig;
  onContactsSelect: (selection: ContactsByOperatorSelection) => void;
  onDownload: (payload: { id: string }) => void;
}

export const FleetStatusCard = ({
  conjunction,
  formatters,
  riskConfig,
  onContactsSelect,
  onDownload,
}: FleetStatusCardProps) => {
  const navigate = useNavigate();
  const riskValue = getNestedValue(conjunction, ["riskLevel"]);
  const riskTone = getRiskTone(riskValue);
  const riskLevel = getRiskLabel(riskValue);
  const rowId = getConjunctionId(conjunction);
  const actionData = useMemo(
    () => ({ id: rowId, row: { original: conjunction } }),
    [conjunction, rowId],
  );
  const cardActions = useMemo<TableActionType[]>(
    () => [
      TABLE_ACTION.DOWNLOAD,
      TABLE_ACTION.INFO,
      TABLE_ACTION.EVENT,
      TABLE_ACTION.PLOTS,
      (data) => (
        <ContactsByOperatorButton data={data} onSelect={onContactsSelect} />
      ),
    ],
    [onContactsSelect],
  );
  const actionContext = useMemo(
    () => ({
      actions: cardActions,
      base: "fleet",
      downloadData: onDownload,
    }),
    [cardActions, onDownload],
  );
  const eventId = getRecordEventId(conjunction);

  const timeToTcaPillClass = "bg-red-50 text-red-700";

  const summaryFields: ConjunctionSummaryCardField[] = [
    {
      label: "MISS DIST.",
      value: getFormattedDisplayValue(
        conjunction,
        "missDistance.value",
        formatters,
      ),
      strong: true,
      tone: getConjunctionMetricTone(
        getNestedValue(conjunction, ["missDistance"]),
        riskConfig,
        "minDist",
      ),
    },
    {
      label: "RADIAL",
      value: getFormattedDisplayValue(
        conjunction,
        "radialDistance.value",
        formatters,
      ),
      strong: true,
      tone: getConjunctionMetricTone(
        getNestedValue(conjunction, ["radialDistance"]),
        riskConfig,
        "radDist",
      ),
    },
    {
      label: "ALONG",
      value: getFormattedDisplayValue(
        conjunction,
        "alongTrackDistance.value",
        formatters,
      ),
      strong: true,
      tone: getConjunctionMetricTone(
        getNestedValue(conjunction, ["alongTrackDistance"]),
        riskConfig,
        "alongDist",
      ),
    },
    {
      label: "CROSS",
      value: getFormattedDisplayValue(
        conjunction,
        "crossTrackDistance.value",
        formatters,
      ),
      strong: true,
      tone: getConjunctionMetricTone(
        getNestedValue(conjunction, ["crossTrackDistance"]),
        riskConfig,
        "crossDist",
      ),
    },
    {
      label: "POC",
      value: getFormattedDisplayValue(
        conjunction,
        "collisionProbability.value",
        formatters,
      ),
      strong: true,
      tone: getProbabilityFamilyTone(
        getNestedValue(conjunction, ["collisionProbability"]),
        riskConfig,
      ),
    },
    {
      label: "SPOC",
      value: getFormattedDisplayValue(
        conjunction,
        "scaledCollisionProbability.value",
        formatters,
      ),
      strong: true,
      tone: getProbabilityFamilyTone(
        getNestedValue(conjunction, ["collisionProbability"]),
        riskConfig,
      ),
    },
    {
      label: "MPOC",
      value: getFormattedDisplayValue(
        conjunction,
        "maximumCollisionProbability.value",
        formatters,
      ),
      strong: true,
      tone: getProbabilityFamilyTone(
        getNestedValue(conjunction, ["collisionProbability"]),
        riskConfig,
      ),
    },
  ];

  return (
    <ConjunctionSummaryCard
      accentClassName={riskAccentClassName[riskTone]}
      actions={
        <>
          {cardActions.map((action, index) =>
            typeof action === "function" ? (
              <Fragment key={index}>
                {action(actionData, actionContext)}
              </Fragment>
            ) : (
              <ActionsColumnItem
                action={action}
                context={actionContext}
                data={actionData}
                key={`${action}-${rowId}`}
              />
            ),
          )}
        </>
      }
      className={riskCardClassName[riskTone]}
      headerPill={
        <div className="flex items-center gap-2">
          <span
            className={`rounded-full px-2.5 py-1 text-xs font-semibold uppercase ${riskPillClassName[riskTone]}`}
          >
            {riskLevel}
          </span>
          <span className="rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-slate-600 ring-1 ring-slate-200">
            {withTimeSystem("Execution Epoch")}:{" "}
            {formatDate(getNestedDisplayValue(conjunction, ["executionEpoch"]))}
          </span>
          <span
            className={`rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-slate-200 ${
              timeToTcaPillClass
            }`}
          >
            {withTimeSystem("TCA")}:{" "}
            {formatDate(
              getNestedDisplayValue(conjunction, ["timeOfCloseApproach"]),
            )}
          </span>
        </div>
      }
      onClick={eventId ? () => navigate(`/event/${eventId}/info`) : undefined}
      onKeyDown={(event) => {
        if (eventId && (event.key === "Enter" || event.key === " ")) {
          event.preventDefault();
          navigate(`/event/${eventId}/info`);
        }
      }}
      primaryObject={
        <div
          className="inline-flex w-fit"
          onClick={(event) => event.stopPropagation()}
        >
          <CompactObject conjunction={conjunction} orbitElement="target" />
        </div>
      }
      secondaryObject={
        <div
          className="inline-flex w-fit"
          onClick={(event) => event.stopPropagation()}
        >
          <CompactObject conjunction={conjunction} orbitElement="chaser" />
        </div>
      }
      summaryFields={summaryFields}
    />
  );
};
